import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CourseV2, TeamMember } from '../../types';
import { courseV2Api, teamApi } from '../../services/api';
import Modal from '../../components/Modal';
import { PlusCircleIcon, PencilIcon, TrashIcon, PlusIcon, ViewGridIcon, ViewListIcon } from '../../components/icons';
import { useTheme } from '../../contexts/ThemeContext';
import AdminButton from '../../components/admin/AdminButton';

const ManageCourseV2Page: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<CourseV2[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  
  const [currentCourse, setCurrentCourse] = useState<Partial<CourseV2>>({
    title: '',
    examType: 'IB',
    subjects: [''],
    image: '',
    description: '',
    features: [''],
    syllabusSummary: [''],
    duration: 'Customized based on student needs',
    mode: 'One-on-One',
    price: { hourly: undefined, package: undefined },
    instructor: undefined,
    highlights: [''],
    status: 'draft'
  });

  const examTypes = ['IB', 'IGCSE', 'CBSE', 'AP', 'SAT', 'JEE', 'NEET', 'Other'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [coursesResponse, teamResponse] = await Promise.all([
        courseV2Api.getAll(),
        teamApi.getAll()
      ]);
      setCourses(coursesResponse.data.data.courses);
      setTeamMembers(teamResponse.data.data.members);
      setError(null);
    } catch (err) {
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  const openModalForNew = () => {
    setIsEditing(false);
    setCurrentCourse({
      title: '',
      examType: 'IB',
      subjects: [''],
      image: '',
      description: '',
      features: [''],
      syllabusSummary: [''],
      duration: 'Customized based on student needs',
      mode: 'One-on-One',
      price: { hourly: undefined, package: undefined },
      instructor: undefined,
      highlights: [''],
      status: 'draft'
    });
    setIsModalOpen(true);
  };

  const openModalForEdit = (course: CourseV2) => {
    setIsEditing(true);
    const instructorId = typeof course.instructor === 'object' ? course.instructor._id : course.instructor;
    setCurrentCourse({
      ...course,
      instructor: instructorId as any
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentCourse({});
  };

  const handleDelete = async (courseId: string) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    try {
      await courseV2Api.delete(courseId);
      await fetchData();
    } catch (err) {
      alert('Failed to delete course.');
    }
  };

  const handleArrayFieldChange = (
    field: 'subjects' | 'features' | 'syllabusSummary' | 'highlights',
    index: number,
    value: string
  ) => {
    setCurrentCourse(prev => {
      const updatedArray = [...(prev[field] || [])];
      updatedArray[index] = value;
      return { ...prev, [field]: updatedArray };
    });
  };

  const addArrayField = (field: 'subjects' | 'features' | 'syllabusSummary' | 'highlights') => {
    setCurrentCourse(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), '']
    }));
  };

  const removeArrayField = (field: 'subjects' | 'features' | 'syllabusSummary' | 'highlights', index: number) => {
    setCurrentCourse(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCourse) return;
    
    setSaving(true);
    try {
      // Filter out empty strings from arrays
      const payload = {
        ...currentCourse,
        subjects: currentCourse.subjects?.filter(s => s.trim()),
        features: currentCourse.features?.filter(f => f.trim()),
        syllabusSummary: currentCourse.syllabusSummary?.filter(s => s.trim()),
        highlights: currentCourse.highlights?.filter(h => h.trim())
      };

      if (isEditing && currentCourse._id) {
        await courseV2Api.update(currentCourse._id, payload);
      } else {
        await courseV2Api.create(payload);
      }
      await fetchData();
      closeModal();
    } catch (err) {
      alert('Failed to save course.');
    } finally {
      setSaving(false);
    }
  };

  const ArrayFieldInput = ({ 
    label, 
    field, 
    placeholder 
  }: { 
    label: string; 
    field: 'subjects' | 'features' | 'syllabusSummary' | 'highlights';
    placeholder: string;
  }) => {
    const fieldArray = currentCourse[field] || [];
    
    return (
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>
          {label}
        </label>
        {fieldArray.map((item, index) => (
          <div key={`${field}-${index}`} className="flex gap-2 mb-2">
            <input
              type="text"
              value={item}
              onChange={(e) => handleArrayFieldChange(field, index, e.target.value)}
              className="flex-1 p-2 rounded border"
              placeholder={placeholder}
              style={{
                backgroundColor: colors.background.default,
                borderColor: colors.border.main,
                color: colors.text.primary
              }}
            />
            <button
              type="button"
              onClick={() => removeArrayField(field, index)}
              className="p-2 rounded hover:opacity-80 transition-opacity"
              style={{ backgroundColor: colors.error.main, color: 'white' }}
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addArrayField(field)}
          className="flex items-center gap-1 text-sm hover:opacity-80 transition-opacity"
          style={{ color: colors.primary.main }}
        >
          <PlusIcon className="w-4 h-4" />
          Add {label.slice(0, -1)}
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold" style={{ color: colors.text.primary }}>
          Manage Courses V2
        </h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center rounded-lg p-1" style={{ backgroundColor: colors.background.paper }}>
            <button
              onClick={() => setViewMode('grid')}
              className="p-2 rounded-lg transition-all duration-300"
              style={{
                background: viewMode === 'grid' ? `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)` : 'transparent',
                color: viewMode === 'grid' ? 'white' : colors.text.secondary
              }}
            >
              <ViewGridIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className="p-2 rounded-lg transition-all duration-300"
              style={{
                background: viewMode === 'table' ? `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)` : 'transparent',
                color: viewMode === 'table' ? 'white' : colors.text.secondary
              }}
            >
              <ViewListIcon className="w-5 h-5" />
            </button>
          </div>
          <AdminButton onClick={openModalForNew} size="md">
            <div className="flex items-center">
              <PlusCircleIcon className="w-5 h-5 mr-2" />
              Add New Course
            </div>
          </AdminButton>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[100px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: colors.primary.main }}></div>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <p className="mb-4" style={{ color: colors.error.main }}>{error}</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <div
              key={course._id}
              className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
              style={{ backgroundColor: colors.background.paper }}
            >
              <div className="aspect-video relative">
                <img
                  src={course.image || 'https://via.placeholder.com/400x225?text=No+Image'}
                  alt={course.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 left-2">
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: colors.accent.warmYellow,
                      color: colors.primary.contrastText
                    }}
                  >
                    {course.examType}
                  </span>
                </div>
                <div className="absolute top-2 right-2">
                  <span 
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      course.status === 'published' ? 'bg-green-500' : 'bg-gray-500'
                    } text-white`}
                  >
                    {course.status}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-2" style={{ color: colors.text.primary }}>
                  {course.title}
                </h3>
                <p className="text-sm mb-4 line-clamp-2" style={{ color: colors.text.secondary }}>
                  {course.description}
                </p>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm" style={{ color: colors.text.secondary }}>
                    {course.mode}
                  </span>
                  {course.price?.hourly && (
                    <span className="font-medium" style={{ color: colors.primary.main }}>
                      ${course.price.hourly}/hr
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/coursesv2/${course._id}`)}
                    className="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    style={{
                      backgroundColor: `${colors.primary.main}20`,
                      color: colors.primary.main
                    }}
                  >
                    View
                  </button>
                  <button
                    onClick={() => openModalForEdit(course)}
                    className="p-2 rounded-lg transition-colors"
                    style={{
                      backgroundColor: `${colors.primary.main}20`,
                      color: colors.primary.main
                    }}
                    title="Edit Course"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(course._id)}
                    className="p-2 rounded-lg transition-colors"
                    style={{
                      backgroundColor: `${colors.error.main}20`,
                      color: colors.error.main
                    }}
                    title="Delete Course"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg shadow-xl overflow-x-auto" style={{ backgroundColor: colors.background.paper }}>
          <table className="w-full text-sm text-left">
            <thead className="text-xs uppercase" style={{ backgroundColor: colors.background.default }}>
              <tr>
                <th scope="col" className="px-6 py-3">Title</th>
                <th scope="col" className="px-6 py-3">Exam Type</th>
                <th scope="col" className="px-6 py-3">Mode</th>
                <th scope="col" className="px-6 py-3">Price (Hourly)</th>
                <th scope="col" className="px-6 py-3">Instructor</th>
                <th scope="col" className="px-6 py-3">Status</th>
                <th scope="col" className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((course) => (
                <tr key={course._id} className="border-b" style={{ borderColor: colors.border.light }}>
                  <td className="px-6 py-4 font-medium" style={{ color: colors.text.primary }}>
                    {course.title}
                  </td>
                  <td className="px-6 py-4" style={{ color: colors.text.secondary }}>
                    {course.examType}
                  </td>
                  <td className="px-6 py-4" style={{ color: colors.text.secondary }}>
                    {course.mode}
                  </td>
                  <td className="px-6 py-4" style={{ color: colors.text.secondary }}>
                    {course.price?.hourly ? `$${course.price.hourly}` : '-'}
                  </td>
                  <td className="px-6 py-4" style={{ color: colors.text.secondary }}>
                    {typeof course.instructor === 'object' ? course.instructor.name : ''}
                  </td>
                  <td className="px-6 py-4">
                    <span 
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        course.status === 'published' ? 'bg-green-500' : 'bg-gray-500'
                      } text-white`}
                    >
                      {course.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button
                      onClick={() => navigate(`/coursesv2/${course._id}`)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      View
                    </button>
                    <button
                      onClick={() => openModalForEdit(course)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(course._id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <Modal 
          isOpen={isModalOpen} 
          onClose={closeModal} 
          title={isEditing ? 'Edit Course' : 'Add New Course'}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>
                  Title
                </label>
                <input
                  type="text"
                  value={currentCourse.title || ''}
                  onChange={(e) => setCurrentCourse({ ...currentCourse, title: e.target.value })}
                  required
                  className="w-full p-2 rounded border"
                  style={{
                    backgroundColor: colors.background.default,
                    borderColor: colors.border.main,
                    color: colors.text.primary
                  }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>
                  Exam Type
                </label>
                <select
                  value={currentCourse.examType || 'IB'}
                  onChange={(e) => setCurrentCourse({ ...currentCourse, examType: e.target.value as any })}
                  className="w-full p-2 rounded border"
                  style={{
                    backgroundColor: colors.background.default,
                    borderColor: colors.border.main,
                    color: colors.text.primary
                  }}
                >
                  {examTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>
                Description
              </label>
              <textarea
                value={currentCourse.description || ''}
                onChange={(e) => setCurrentCourse({ ...currentCourse, description: e.target.value })}
                required
                rows={3}
                maxLength={200}
                className="w-full p-2 rounded border"
                style={{
                  backgroundColor: colors.background.default,
                  borderColor: colors.border.main,
                  color: colors.text.primary
                }}
              />
              <p className="text-xs mt-1" style={{ color: colors.text.secondary }}>
                {currentCourse.description?.length || 0}/200 characters
              </p>
            </div>

            <ArrayFieldInput 
              label="Subjects" 
              field="subjects" 
              placeholder="e.g., Mathematics" 
            />
            
            <ArrayFieldInput 
              label="Key Features" 
              field="features" 
              placeholder="e.g., Personalized study plan" 
            />
            
            <ArrayFieldInput 
              label="Syllabus Topics" 
              field="syllabusSummary" 
              placeholder="e.g., Algebra and Functions" 
            />
            
            <ArrayFieldInput 
              label="Highlights" 
              field="highlights" 
              placeholder="e.g., 24/7 doubt support" 
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>
                  Mode
                </label>
                <select
                  value={currentCourse.mode || 'One-on-One'}
                  onChange={(e) => setCurrentCourse({ ...currentCourse, mode: e.target.value as any })}
                  className="w-full p-2 rounded border"
                  style={{
                    backgroundColor: colors.background.default,
                    borderColor: colors.border.main,
                    color: colors.text.primary
                  }}
                >
                  <option value="One-on-One">One-on-One</option>
                  <option value="Small Group">Small Group</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>
                  Status
                </label>
                <select
                  value={currentCourse.status || 'draft'}
                  onChange={(e) => setCurrentCourse({ ...currentCourse, status: e.target.value as any })}
                  className="w-full p-2 rounded border"
                  style={{
                    backgroundColor: colors.background.default,
                    borderColor: colors.border.main,
                    color: colors.text.primary
                  }}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>
                  Hourly Price (optional)
                </label>
                <input
                  type="number"
                  value={currentCourse.price?.hourly || ''}
                  onChange={(e) => setCurrentCourse({ 
                    ...currentCourse, 
                    price: { ...currentCourse.price!, hourly: e.target.value ? Number(e.target.value) : undefined }
                  })}
                  min="0"
                  step="0.01"
                  placeholder="e.g., 50"
                  className="w-full p-2 rounded border"
                  style={{
                    backgroundColor: colors.background.default,
                    borderColor: colors.border.main,
                    color: colors.text.primary
                  }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>
                  Package Price (optional)
                </label>
                <input
                  type="number"
                  value={currentCourse.price?.package || ''}
                  onChange={(e) => setCurrentCourse({ 
                    ...currentCourse, 
                    price: { ...currentCourse.price!, package: e.target.value ? Number(e.target.value) : undefined }
                  })}
                  min="0"
                  step="0.01"
                  placeholder="e.g., 500"
                  className="w-full p-2 rounded border"
                  style={{
                    backgroundColor: colors.background.default,
                    borderColor: colors.border.main,
                    color: colors.text.primary
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>
                Duration
              </label>
              <input
                type="text"
                value={currentCourse.duration || 'Customized based on student needs'}
                onChange={(e) => setCurrentCourse({ ...currentCourse, duration: e.target.value })}
                className="w-full p-2 rounded border"
                style={{
                  backgroundColor: colors.background.default,
                  borderColor: colors.border.main,
                  color: colors.text.primary
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>
                Instructor
              </label>
              <select
                value={currentCourse.instructor || ''}
                onChange={(e) => setCurrentCourse({ ...currentCourse, instructor: e.target.value as any })}
                required
                className="w-full p-2 rounded border"
                style={{
                  backgroundColor: colors.background.default,
                  borderColor: colors.border.main,
                  color: colors.text.primary
                }}
              >
                <option value="" disabled>Select instructor</option>
                {teamMembers.map(member => (
                  <option key={member._id} value={member._id}>{member.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>
                Image URL
              </label>
              <input
                type="text"
                value={currentCourse.image || ''}
                onChange={(e) => setCurrentCourse({ ...currentCourse, image: e.target.value })}
                className="w-full p-2 rounded border"
                placeholder="https://example.com/image.jpg"
                style={{
                  backgroundColor: colors.background.default,
                  borderColor: colors.border.main,
                  color: colors.text.primary
                }}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 rounded transition-colors"
                style={{
                  backgroundColor: colors.background.default,
                  color: colors.text.secondary
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 rounded text-white transition-colors"
                style={{
                  backgroundColor: colors.primary.main,
                  opacity: saving ? 0.5 : 1
                }}
              >
                {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Course')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default ManageCourseV2Page;